import { store } from "../state/store.js";
import { TELEVES_LOGO_SVG, TELEVES_LOGO_PNG_DATAURL } from "../constants/televes.js";
import { getIntermediateFrequencyMHz, getPolBandLabel, getLnbDescription } from "../utils/sat.js";

function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function clean(v) {
  return String(v ?? "").trim();
}

function safeFilePart(s) {
  return String(s ?? "")
    .trim()
    .replace(/[<>:"/\\|?*]+/g, "")
    .replace(/\s+/g, "_");
}

function todayYmd() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function nicePackageName(tp) {
  return clean(tp?.tp_name) || clean(tp?.provider) || clean(tp?.alias_raw) || "Paket";
}

function encText(p) {
  const v = p?.encrypted;
  if (v === true) return "Verschluesselt";
  const s = clean(v).toLowerCase();
  if (!s) return "";
  if (["frei", "free", "false", "0", "fta"].includes(s)) return "Frei";
  if (["true", "1", "encrypted", "verschluesselt", "scrambled"].includes(s)) return "Verschluesselt";
  return clean(v);
}

function isFreeProgram(p) {
  const v = p?.encrypted;
  if (v === false) return true;
  if (v === true) return false;

  const s = clean(v).toLowerCase();
  if (!s) return true;
  if (["frei", "free", "false", "0", "fta", "no", "nein"].includes(s)) return true;
  if (["true", "1", "encrypted", "verschluesselt", "scrambled", "yes", "ja"].includes(s)) return false;
  return true;
}

async function svgToPngDataUrl(svgText, scale = 2) {
  if (typeof canvg === "undefined") return "";
  const canvas = document.createElement("canvas");
  canvas.width = 320 * scale;
  canvas.height = 90 * scale;
  const ctx = canvas.getContext("2d");
  const v = await canvg.Canvg.fromString(ctx, svgText);
  v.resize(canvas.width, canvas.height);
  await v.render();
  return canvas.toDataURL("image/png");
}

async function resolveLogoPngDataUrl() {
  const png = clean(TELEVES_LOGO_PNG_DATAURL);
  if (png.startsWith("data:image/png;base64,")) return png;

  const svg = clean(TELEVES_LOGO_SVG);
  if (svg) return await svgToPngDataUrl(svg, 2);

  return "";
}

function readOptionalInputs() {
  const bearbeiter = clean(document.getElementById("bearbeiterInput")?.value);
  const bauvorhaben = clean(document.getElementById("bauvorhabenInput")?.value);
  const freeOnly = !!document.getElementById("exportFreeOnly")?.checked;
  return { bearbeiter, bauvorhaben, freeOnly };
}

function classifyService(p) {
  const raw = p?.type ?? p?.service_type ?? p?.ServiceType ?? "";
  const s = clean(raw).toLowerCase();

  if (s === "1" || s === "01") return "tv";
  if (s === "2" || s === "02") return "radio";

  if (s.includes("radio")) return "radio";
  if (s.includes("tv") || s.includes("television") || s.includes("fernsehen")) return "tv";

  return "other";
}

function writeHeaderBlock(ws, { title, bearbeiter, bauvorhaben, selectionText, dbText, lnbType }) {
  ws.addRow([]);
  ws.addRow([]);
  const rowTitle = ws.addRow([title]).number;
  ws.addRow([]);
  if (bearbeiter) ws.addRow(["Bearbeiter", bearbeiter]);
  if (bauvorhaben) ws.addRow(["Bauvorhaben", bauvorhaben]);
  const rowLnb = ws.addRow([`LNB: ${getLnbDescription(lnbType)} | ZF in MHz`]).number;
  ws.mergeCells(`A${rowLnb}:H${rowLnb}`);
  ws.addRow([]);
  const rowSelection = ws.addRow([selectionText]).number;
  ws.mergeCells(`G${rowSelection}:H${rowSelection}`);
  ws.getCell(`G${rowSelection}`).value = dbText;
  ws.getCell(`G${rowSelection}`).alignment = { vertical: "middle", horizontal: "right" };
  ws.addRow([]);
  return { rowTitle, rowSelection, nextRow: ws.lastRow.number + 1 };
}

export async function exportSelectionWithLogo() {
  if (typeof ExcelJS === "undefined") {
    throw new Error("ExcelJS ist nicht geladen. Pruefe libs/exceljs.min.js");
  }

  const keys = Array.from(store.selection.keys());
  if (!keys.length) {
    alert("Keine Transponder ausgewaehlt.");
    return;
  }

  const { bearbeiter, bauvorhaben, freeOnly } = readOptionalInputs();
  const lnbType = store.ui.lnbType;

  let tvCount = 0;
  let radioCount = 0;
  let progTotal = 0;
  for (const k of keys) {
    const progs = store.programsByTpKey.get(k) || [];
    const exportProgs = freeOnly ? progs.filter(isFreeProgram) : progs;
    for (const p of exportProgs) {
      progTotal++;
      const kind = classifyService(p);
      if (kind === "tv") tvCount++;
      else if (kind === "radio") radioCount++;
    }
  }

  const wb = new ExcelJS.Workbook();
  wb.creator = "SAT Tool";
  wb.created = new Date();

  const ws = wb.addWorksheet("Auswahl", {
    properties: { defaultRowHeight: 20 },
  });

  let rowTitle;
  let rowSelection;
  let rowTpSectionTitle;
  let rowTpHeader;
  let rowPrgSectionTitle;
  let rowPrgHeader;

  const CM = 0.3937;
  ws.pageSetup = {
    paperSize: 9,
    orientation: "portrait",
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    showGridLines: false,
    margins: {
      left: CM,
      right: CM,
      top: CM,
      bottom: CM,
      header: CM,
      footer: CM,
    },
  };

  ws.headerFooter.oddHeader = "&R&D";
  ws.headerFooter.oddFooter = "&RSeite &P / &N";

  ws.getColumn(1).width = 28;
  ws.getColumn(2).width = 12;
  ws.getColumn(3).width = 10;
  ws.getColumn(4).width = 14;
  ws.getColumn(5).width = 12;
  ws.getColumn(6).width = 12;
  ws.getColumn(7).width = 14;
  ws.getColumn(8).width = 12;

  try {
    const logoDataUrl = await resolveLogoPngDataUrl();
    if (logoDataUrl) {
      const base64 = logoDataUrl.split(",")[1];
      const imgId = wb.addImage({ base64, extension: "png" });
      ws.addImage(imgId, {
        tl: { col: 6.8, row: 1.8 },
        ext: { width: 110, height: 32 },
      });
    }
  } catch (e) {
    console.warn("Logo konnte nicht gerendert werden:", e);
  }

  const hdr = writeHeaderBlock(ws, {
    title: freeOnly ? "SAT Transponder Uebersicht (nur freie Programme)" : "SAT Transponder Uebersicht",
    bearbeiter,
    bauvorhaben,
    selectionText: `Auswahl: ${keys.length} Transponder | Programme: ${progTotal} | TV: ${tvCount} | Radio: ${radioCount}`,
    dbText: `DB: ${store.db.date || "Datum unbekannt"}`,
    lnbType,
  });
  rowTitle = hdr.rowTitle;
  rowSelection = hdr.rowSelection;

  let r = hdr.nextRow;

  rowTpSectionTitle = r;
  ws.getCell(`A${r}`).value = "Transponder";
  r++;

  rowTpHeader = r;
  ws.getRow(r).values = ["Paket", "Frequenz", "ZF", "Pol", "Modulation", "Symbolrate", "TS-ID", "ON-ID"];
  ws.getRow(r).alignment = { vertical: "middle", horizontal: "left" };
  r++;

  for (const key of keys) {
    const tp = store.tpByKey.get(key);
    if (!tp) continue;

    ws.getRow(r).values = [
      nicePackageName(tp),
      tp.frequency_MHz ?? "",
      getIntermediateFrequencyMHz(tp.frequency_MHz, lnbType) ?? "",
      getPolBandLabel(tp.pol, tp.frequency_MHz) ?? "",
      tp.dvb ?? "",
      tp.symbolrate_kSps ?? "",
      tp.tsid ?? "",
      tp.onid ?? "",
    ];
    ws.getRow(r).alignment = { vertical: "middle", horizontal: "left", wrapText: true };
    r++;
  }

  r += 2;

  rowPrgSectionTitle = r;
  ws.getCell(`A${r}`).value = "Programme";
  r++;

  rowPrgHeader = r;
  ws.getRow(r).values = ["Paket", "Programme", "Sprache", "Programmtyp", "Aufloesung", "Verschluesselung"];
  ws.getRow(r).alignment = { vertical: "middle", horizontal: "left" };
  r++;

  for (const key of keys) {
    const tp = store.tpByKey.get(key);
    if (!tp) continue;

    const pkg = nicePackageName(tp);
    const progs = store.programsByTpKey.get(key) || [];
    const exportProgs = freeOnly ? progs.filter(isFreeProgram) : progs;

    if (!exportProgs.length) {
      ws.getRow(r).values = [pkg, freeOnly ? "(keine freien Programme gefunden)" : "(keine Programme gefunden)", "", "", "", ""];
      ws.getRow(r).alignment = { vertical: "middle", horizontal: "left", wrapText: true };
      r++;
      ws.addRow([]);
      r++;
      continue;
    }

    let first = true;
    for (const p of exportProgs) {
      const name =
        clean(p.program_name) ||
        clean(p.service) ||
        clean(p.name) ||
        clean(p.service_name) ||
        "";

      const lang = clean(p.language || p.lang || p.Lang || p.Sprache);
      const typ = clean(p.type);
      const res = clean(p.resolution);
      const enc = encText(p);

      ws.getRow(r).values = [first ? pkg : "", name, lang, typ, res, enc];
      ws.getRow(r).alignment = { vertical: "middle", horizontal: "left", wrapText: true };
      r++;
      first = false;
    }

    ws.addRow([]);
    r++;
  }

  ws.pageSetup.printArea = `A1:G${r}`;

  ws.eachRow((row) => {
    row.eachCell((cell) => {
      cell.font = { name: "Calibri", size: 10 };
    });
  });

  ws.getRow(rowTitle).font = { name: "Calibri", size: 14, bold: true };
  ws.getRow(rowTpSectionTitle).font = { name: "Calibri", size: 12, bold: true };
  ws.getRow(rowTpHeader).font = { name: "Calibri", size: 11, bold: true };
  ws.getRow(rowPrgSectionTitle).font = { name: "Calibri", size: 12, bold: true };
  ws.getRow(rowPrgHeader).font = { name: "Calibri", size: 11, bold: true };

  ws.getCell(`F${rowSelection}`).font = { name: "Calibri", size: 8 };

  const dateStr = todayYmd();
  const tpCount = keys.length;
  const bv = clean(bauvorhaben);

  let outName;
  if (bv) {
    outName = `${dateStr}_TP-Liste_${safeFilePart(bv)}_${tpCount}TP.xlsx`;
  } else {
    outName = `${dateStr}_Transponderliste_${tpCount}TP.xlsx`;
  }

  const buf = await wb.xlsx.writeBuffer();

  downloadBlob(
    new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    outName
  );
}
